import { useSelector } from 'react-redux'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { getAdminSharedDictionary, type SharedDictionaryQuery } from '@/api/dictionary'
import type { AdminSharedDictionaryResponse } from '@/api/generated'
import { selectCurrentEnterprise } from '@/store/features/user/userSlice'

// Scoped by enterprise: the QueryClient is a module singleton and no tenant transition clears it,
// so an unscoped key would serve tenant A's entries to an admin who switched to tenant B.
// Invalidate through the prefix, so one tenant's writes never mark another tenant's pages stale.
export const sharedDictionaryQueryPrefix = (enterpriseId: string) => ['shared-dictionary', enterpriseId] as const

export const sharedDictionaryQueryKey = (enterpriseId: string, query: SharedDictionaryQuery) =>
  [...sharedDictionaryQueryPrefix(enterpriseId), query] as const

// The queue's base query. Shared so the count badge and the table's first page stay one shape.
export const PENDING_SHARED_QUERY: SharedDictionaryQuery = {
  search: '',
  limit: 25,
  offset: 0,
  status: 'pending',
}

export const useSharedDictionary = (query: SharedDictionaryQuery) => {
  const enterpriseId = useSelector(selectCurrentEnterprise)?.id ?? ''

  return useQuery<AdminSharedDictionaryResponse>({
    queryKey: sharedDictionaryQueryKey(enterpriseId, query),
    queryFn: async ({ signal }) => (await getAdminSharedDictionary(query, signal)).data,
    enabled: Boolean(enterpriseId),
    // Paging and typing both change the key. Without this the table empties to its skeleton on
    // every keystroke; the previous page stays put while the next one loads.
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  })
}
